import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { UserPlus, Trash, ShieldCheck, User as UserIcon, X, Pencil, Key } from "@phosphor-icons/react";

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = () => {
    setLoading(true);
    api.get("/users").then((r) => setUsers(r.data)).finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const onDelete = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.email} removed`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const isAdmin = me?.role === "admin";

  return (
    <AdminLayout>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono-sms text-xs uppercase tracking-[0.3em] text-[var(--sms-primary)] mb-3">Team</div>
          <h1 className="font-display text-5xl font-black tracking-tighter">Admins &amp; Managers.</h1>
          <p className="text-slate-500 mt-2">{users.length} {users.length === 1 ? "user" : "users"} · Managers can apply discounts; only admins can manage users.</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowCreate(true)} data-testid="btn-create-user" className="sms-btn-primary inline-flex items-center gap-2">
            <UserPlus size={16} weight="bold" /> Add user
          </button>
        )}
      </div>

      <div className="border border-slate-200" data-testid="users-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <th className="text-left px-4 py-3 font-medium">Name</th>
              <th className="text-left px-4 py-3 font-medium">Email</th>
              <th className="text-left px-4 py-3 font-medium">Role</th>
              <th className="text-left px-4 py-3 font-medium">Created</th>
              <th className="text-right px-4 py-3 font-medium"> </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400 font-mono-sms text-xs uppercase tracking-[0.2em]">Loading…</td></tr>
            )}
            {!loading && users.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">No users yet.</td></tr>
            )}
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-t border-slate-200 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {u.role === "admin" ? <ShieldCheck size={14} weight="duotone" className="text-[var(--sms-primary)]" /> : <UserIcon size={14} weight="duotone" className="text-slate-400" />}
                    <span className="font-medium">{u.name || "—"}</span>
                    {u.id === me?.id && <span className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-400">(you)</span>}
                  </div>
                </td>
                <td className="px-4 py-3 font-mono-sms text-xs">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`sms-badge ${u.role === "admin" ? "sms-badge-approved" : "sms-badge-pending"}`} data-testid={`role-${u.email}`}>
                    {u.role}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono-sms text-xs text-slate-500">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  {isAdmin && (
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => setEditing(u)}
                        data-testid={`btn-edit-${u.email}`}
                        className="text-slate-400 hover:text-[var(--sms-primary)]"
                        title="Edit profile / reset password"
                      >
                        <Pencil size={14} weight="bold" />
                      </button>
                      {u.id !== me?.id && (
                        <button onClick={() => onDelete(u)} data-testid={`btn-delete-${u.email}`} className="text-slate-400 hover:text-[var(--sms-danger)]" title="Delete user">
                          <Trash size={14} weight="bold" />
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateUserModal onClose={() => setShowCreate(false)} onCreated={() => { refresh(); setShowCreate(false); }} />
      )}

      {editing && (
        <EditUserModal
          user={editing}
          isSelf={editing.id === me?.id}
          onClose={() => setEditing(null)}
          onSaved={() => { refresh(); setEditing(null); }}
        />
      )}
    </AdminLayout>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "manager" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success(`${form.email} created`);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not create user");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xl" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white border border-slate-900 shadow-2xl w-full max-w-md p-6" data-testid="create-user-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)]">New user</div>
            <div className="font-display text-2xl font-black tracking-tighter mt-1">Add a teammate</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} weight="bold" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Full name *</div>
            <input className="sms-input" required data-testid="new-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Email *</div>
            <input type="email" className="sms-input font-mono-sms" required data-testid="new-user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })} />
          </div>
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Temporary password * <span className="text-slate-400">(min 8 chars)</span></div>
            <input type="text" className="sms-input font-mono-sms" required minLength={8} data-testid="new-user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Role</div>
            <div className="flex items-center gap-1 border border-slate-200">
              {["staff", "manager", "admin"].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setForm({ ...form, role: r })}
                  data-testid={`new-user-role-${r}`}
                  className={`flex-1 px-4 py-2 font-mono-sms text-[10px] uppercase tracking-[0.2em] ${form.role === r ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                >
                  {r}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-slate-400 mt-2">
              Managers can apply discounts and review applications. Admins can also manage users and settings.
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-5 mt-5 border-t border-slate-200">
          <button type="button" onClick={onClose} className="sms-btn-secondary text-xs">Cancel</button>
          <button type="submit" disabled={busy} data-testid="confirm-create-user" className="sms-btn-primary">
            {busy ? "Creating…" : "Create user"}
          </button>
        </div>
      </form>
    </div>
  );
}

function EditUserModal({ user, isSelf, onClose, onSaved }) {
  const [form, setForm] = useState({ name: user.name || "", role: user.role || "manager" });
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetting, setResetting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const patch = {};
      if (form.name !== user.name) patch.name = form.name.trim();
      if (form.role !== user.role && !isSelf) patch.role = form.role;
      if (Object.keys(patch).length === 0) {
        toast.info("Nothing changed");
        setBusy(false);
        return;
      }
      await api.patch(`/users/${user.id}`, patch);
      toast.success("Profile updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async () => {
    if (pw.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (!window.confirm(`Reset password for ${user.email}? They will need the new password to sign in.`)) return;
    setResetting(true);
    try {
      await api.patch(`/users/${user.id}`, { password: pw });
      toast.success("Password reset · share the new credentials with the user");
      setPw("");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xl" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-slate-900 shadow-2xl w-full max-w-md p-6" data-testid="edit-user-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)]">Edit user</div>
            <div className="font-display text-2xl font-black tracking-tighter mt-1">{user.email}</div>
            {isSelf && <div className="text-[11px] text-slate-400 mt-1">You can update your name but not your own role.</div>}
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} weight="bold" /></button>
        </div>

        {/* Profile section */}
        <form onSubmit={submit} className="space-y-4">
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Full name</div>
            <input className="sms-input" required data-testid="edit-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Role</div>
            <div className="flex items-center gap-1 border border-slate-200">
              {["staff", "manager", "admin"].map((r) => (
                <button
                  key={r}
                  type="button"
                  disabled={isSelf}
                  onClick={() => setForm({ ...form, role: r })}
                  data-testid={`edit-user-role-${r}`}
                  className={`flex-1 px-4 py-2 font-mono-sms text-[10px] uppercase tracking-[0.2em] disabled:opacity-50 ${form.role === r ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200">
            <button type="submit" disabled={busy} data-testid="save-user-edit" className="sms-btn-primary">{busy ? "Saving…" : "Save profile"}</button>
          </div>
        </form>

        {/* Password reset */}
        <div className="mt-6 pt-5 border-t border-slate-200">
          <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3 inline-flex items-center gap-2">
            <Key size={12} weight="duotone" /> Reset password
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="sms-input font-mono-sms"
              data-testid="edit-user-password"
              placeholder="New password (min 8 characters)"
              value={pw}
              minLength={8}
              onChange={(e) => setPw(e.target.value)}
            />
            <button
              type="button"
              onClick={resetPassword}
              disabled={resetting || pw.length < 8}
              data-testid="confirm-reset-password"
              className="sms-btn-secondary text-xs whitespace-nowrap"
            >
              {resetting ? "Resetting…" : "Reset"}
            </button>
          </div>
          <div className="text-[11px] text-slate-400 mt-2">
            The user will need this new password to sign in. Share it via a secure channel.
          </div>
        </div>
      </div>
    </div>
  );
}
